import React from 'react';
import { cn } from '@/utils/cn';

interface AlertProps {
  children: React.ReactNode;
  variant?: 'info' | 'success' | 'warning' | 'error';
  onDismiss?: () => void;
  className?: string;
}

// Severity → ARIA live-region role mapping per WAI-ARIA Authoring Practices:
// - `alert` (assertive): error / warning — interrupts the screen reader's
//   current speech because the message blocks the user's path.
// - `status` (polite): info / success — queued politely so it doesn't
//   yank focus mid-task.
// Stays a div with role rather than the semantic <output>/<aside> tags
// because Tailwind base styles + the existing dismiss-button slot don't
// fit those elements' default UA presentation.
const VARIANT_ROLE: Record<NonNullable<AlertProps['variant']>, 'alert' | 'status'> = {
  info: 'status',
  success: 'status',
  warning: 'alert',
  error: 'alert',
};

export const Alert: React.FC<AlertProps> = ({
  children,
  variant = 'info',
  onDismiss,
  className,
}) => {
  const variants = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    error: 'bg-red-50 border-red-200 text-red-800',
  };
  const role = VARIANT_ROLE[variant];

  return (
    <div
      role={role}
      // aria-live mirrors the role's implicit politeness so older
      // assistive tech that doesn't honor `role` alone still gets the
      // right urgency. role=alert is implicitly assertive; role=status
      // is implicitly polite. Setting both belt-and-suspenders.
      aria-live={role === 'alert' ? 'assertive' : 'polite'}
      className={cn(
        'border rounded-md p-4',
        variants[variant],
        className
      )}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">{children}</div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss alert"
            className="ml-3 text-sm opacity-70 hover:opacity-100"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};